<template>
	<div>
		<core-layout-content full>
			<core-layout-headline-and-actions>
				<template slot="headline">{{ $t("transactions") }}</template>
				<template slot="actions">
					<j-button
						v-can="'writeTransactions'"
						type="primary"
						@click="$modal.show('addTransaction')"
					>
						<crm-icon name="add" size="small" color="c5" />
						{{ $t("add_transaction") }}
					</j-button>
				</template>
			</core-layout-headline-and-actions>

			<!-- Data Container -->
			<j-card class="bg5">
				<j-data-table
					:header="header"
					:list="computedtransactions"
					:sortable="false"
					:isLoading="isLoading"
					@sort="onSort($event)"
				>
					<template slot="list" slot-scope="{ item }">
						<td v-for="(column, i) in header" :key="i">
							<template v-if="column.field === 'actions'">
								<div class="flex xs-12 xs-right">
									<crm-icon
										name="file-download"
										color="c1"
										class="cu2"
										@click.native="handleDownload(item)"
									/>
									<crm-icon
										v-can="'writeTransactions'"
										name="edit"
										color="c6"
										class="cu2"
										@click.native="handleUpdate(item)"
									/>
								</div>
							</template>
							<template v-else>
								<template v-if="column.field === 'transactionNumber'">
									<router-link
										:to="
											`/${
												$route.params.tenantId
											}/transactions/detail/transaction/${item.id}`
										"
									>
										<span> {{ item[column.field] || "---" }}</span>
									</router-link>
								</template>
								<template v-else>
									{{ item[column.field] || "---" }}
								</template>
							</template>
						</td>
					</template>
					<template slot="empty">{{ $t("errors.no_data") }}</template>
				</j-data-table>
			</j-card>

			<core-modal name="addTransaction" :headline="$t('add_transaction')">
				<transaction-create
					:policyId="id"
					@cancel="$modal.hide('addTransaction')"
					@transaction:added="
						$modal.hide('addTransaction'), fetchTransactions()
					"
				/>
			</core-modal>

			<!-- Update Transaction -->
			<core-modal name="updateTransaction" :headline="$t('update_transaction')">
				<transaction-update
					:policyId="id"
					:transactionId="transactionToUpdateId"
					:initialPayload="transactionToUpdatePayload"
					@transaction:updated="
						$modal.hide('updateTransaction'), fetchTransactions()
					"
					@cancel="$modal.hide('updateTransaction')"
				/>
			</core-modal>
		</core-layout-content>
	</div>
</template>

<script>
import CrmIcon from "@/components/CrmIcon/"
import JButton from "@/components/JButton/"
import JCard from "@/components/JCard/"
import JDataTable from "@/components/JDataTable/"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import CoreModal from "@/modules/core/components/CoreModal"
import TransactionCreate from "../TransactionCreate"
import TransactionUpdate from "../TransactionUpdate"
import dayjs from "dayjs"
import api from "../../api/"
import globalApi from "@/api/gql.js"
import get from "lodash.get"
import { gql } from "@/api/utils.js"

export default {
	components: {
		CrmIcon,
		JButton,
		JCard,
		JDataTable,
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions,
		CoreModal,
		TransactionCreate,
		TransactionUpdate
	},

	data: () => ({
		isLoading: false,
		transactions: [],
		selectedTemplate: null,
		selectedTransaction: null,
		templates: null,
		graphQlVariables: null,

		// When user clicks on edit icon, we save this id here
		transactionToUpdateId: null,
		transactionToUpdatePayload: null
	}),

	computed: {
		// get policy id from router
		id() {
			return this.$route.params.id
		},

		header() {
			return [
				{ text: this.$t("when"), field: "dateTime" },
				{ text: this.$t("amount"), field: "formattedAmount" },
				{ text: this.$t("payment_method"), field: "method" },
				{ text: this.$t("status"), field: "localizedStatus" },
				{ text: this.$t("actions"), field: "actions" }
			]
		},

		computedtransactions() {
			const transactions = this.transactions
			if (!transactions) return []

			// Crunch transactions
			// Sort to compare and order dates correctly
			// Also reverse them so that most recent one is the most top
			return transactions
				.sort((a, b) => {
					let dateA = a.dateTime
					let dateB = b.dateTime
					if (dateA < dateB) return -1
					if (dateA > dateB) return 1
					return 0
				})
				.map(item => ({
					id: item.id,
					dateTime: item.dateTime
						? dayjs(item.dateTime).format(
								this.$store.state.settings.dateTimeFormat
						  )
						: null,
					formattedAmount: item.formattedTransaction,
					amount: item.amount,
					currencyCode: item.currencyCode,
					status: item.status,
					localizedStatus: item.status, // TODO: localized
					method: item.method,
					localizedMethod: item.method
						? this.$t(`payment_methods_enums.${item.method}`)
						: null
				}))
				.reverse()
		}
	},

	methods: {
		async fetchTransactions() {
			this.isLoading = true
			const res = await api.policyTransactions({
				where: { id: this.id }
			})
			this.isLoading = false

			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}

			// Success
			this.transactions = get(res, "data.policies.list[0].transactions", [])
		},

		async downloadPreviewFile() {
			let pdfDrawingQuery = gql`
				query renderPdfDrawingTemplate(
					$templateId: String!
					$input: templateRenderParametersInput
				) {
					renderPdfDrawingTemplate(templateId: $templateId, input: $input) {
						errors
						status
						value
					}
				}
			`

			let apiKey = "renderPdfDrawingTemplate"

			let query = pdfDrawingQuery

			let variables = {
				templateId: "e0208a59-c4f5-4cf7-82bd-7ceeb6046a3b",
				input: {
					name: "data",
					variablesJsonString: `{"transactionWhere1":{"id":"${this.selectedTransaction.id}"}}`
				}
			}

			this.isLoading = true

			const res = await globalApi({ query, variables })

			this.isLoading = false

			if (res.data?.[apiKey]?.errors && res.data?.[apiKey]?.errors?.length) {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: res?.errors?.toString() || res.data?.[apiKey]?.errors
					}
				})
			}

			if (res.data?.[apiKey]?.status === "success") {
				let bytes = new Uint8Array(res.data?.[apiKey]?.value) // pass your byte response to this constructor
				let blob = new Blob([bytes], { type: "application/pdf" }) // change resultByte to bytes
				let link = document.createElement("a")
				link.href = window.URL.createObjectURL(blob)
				link.download = "test.pdf"
				link.click()
			}
		},

		handleDownload(item) {
			this.selectedTransaction = item
			this.downloadPreviewFile()
		},

		handleUpdate(item) {
			this.transactionToUpdateId = item.id
			this.transactionToUpdatePayload = {
				amount: item.amount,
				currencyCode: item.currencyCode,
				status: item.status,
				dateTime: item.dateTime,
				method: item.method
			}

			// Open popup
			this.$modal.show("updateTransaction")
		}
	},

	created() {
		this.fetchTransactions()
	}
}
</script>
